import { useUpdateObjectiveAttribute } from '@/api/glow-up/glow-up.hooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ObjectiveAttribute,
  UpdateObjectiveAttributeRequest,
} from '@repo/types/src/glow-up';
import { Edit } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { label: 'Actif', value: 'active' },
  { label: 'Inactif', value: 'inactive' },
];

type EditObjectiveAttributeProps = {
  attribute: ObjectiveAttribute;
  refetch: () => void;
};

export const EditObjectiveAttribute = ({
  attribute,
  refetch,
}: EditObjectiveAttributeProps) => {
  const mutation = useUpdateObjectiveAttribute();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<UpdateObjectiveAttributeRequest>({
    name: attribute.name,
    status: attribute.status,
  });

  const handleSubmit = () => {
    if (!formData.name?.trim()) {
      toast.error('Veuillez renseigner un nom');
      return;
    }

    mutation.mutate(
      {
        id: attribute.id,
        payload: {
          name: formData.name.trim(),
          status: formData.status ?? 'active',
        },
      },
      {
        onSuccess: (data) => {
          setOpen(false);
          refetch();
          toast.success(
            `L’attribut d’objectif "${data.name}" a été mis à jour`,
            { duration: 3000 },
          );
        },
        onError: () => {
          toast.error(
            `Erreur lors de la mise à jour de l’attribut "${attribute.name}"`,
            { duration: 3000 },
          );
        },
      },
    );
  };

  const isLoading = mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-center">
            Modifier l’attribut d’objectif
          </DialogTitle>
          <DialogDescription className="text-center">
            Ajustez le nom ou le statut de l’attribut
          </DialogDescription>
        </DialogHeader>

        <div className="w-full flex flex-col space-y-4">
          <Input
            type="text"
            placeholder="Nom de l’attribut"
            value={formData.name ?? ''}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, name: event.target.value }))
            }
            disabled={isLoading}
          />

          <Select
            value={formData.status ?? 'active'}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, status: value }))
            }
            disabled={isLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter className="flex gap-2">
          <DialogClose asChild>
            <Button variant="ghost" disabled={isLoading}>
              Annuler
            </Button>
          </DialogClose>
          <Button variant="default" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
